/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, memo, useCallback, useEffect, useRef } from 'react';
import { ApprovalRuleDetail } from 'models/admin/Approval';
import { useCommonModal } from 'hooks/useCommonModal';
import { useTranslation } from 'react-i18next';
import { Code } from 'models/common/CommonCode';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { DataMap } from '@grapecity/wijmo.grid';
import { commonYNcodes } from 'models/common/Common';
import { Button } from '@mui/material';
import { IconButton } from '../../../components/buttons/IconSVG';
import { getApprovalRuleDetails, setApprovalRuleDetails } from '../../../apis/admin/ApprovalRule';
import { CrudCode } from '../../../models/common/Edit';
import { Employee } from 'models/admin/Employee';
import { getCommonCodeNames } from '../../../apis/admin/CommonCode';
import UserModal from '../../../components/modals/common/UserModal';
import useEvent from 'react-use-event-hook';
import { CellRangeEventArgs } from '@grapecity/wijmo.grid';
import { GridStatusCellTemplate } from '../../../components/grids/GridStatusCellRenderer';
import DepartmentModal from '../../../components/modals/common/DepartmentModal';
import { Department } from '../../../models/admin/Department';

interface SearchParamData {
  sRuleId: string;
  callBackFunc?: () => void;
}

const ApprovalLineGrid = (props: SearchParamData) => {
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();

  const [rowData, setRowData] = useState<ApprovalRuleDetail[]>([]);
  const { openMessageBar } = useMessageBar();

  const [rowIndex, setRowIndex] = useState<string>('');
  const [employeeModalOpen, setEmployeeModalOpen] = useState<boolean>(false);
  const [userModalCondition, setUserModalCondition] = useState<any>('');

  const [departmentModalOpen, setDepartmentModalOpen] = useState<boolean>(false);
  const [departmentModalCondition, setDepartmentModalCondition] = useState<any>('');

  const [aprTpDivsList, setAprTpDivsList] = useState<Code[]>([]);
  const [aprLnRoleList, setAprLnRoleList] = useState<Code[]>([]);
  const [flexRef, setFlexRef] = useState<any>();
  const gridRef = useRef<any>();
  const [code, setCode] = useState<any>();

  //init
  useEffect(() => {
    console.log('--------------------------------------- ApprovalLineGrid...');
    getCommonCodes();
  }, []);

  //props init
  useEffect(() => {
    fnSearchApprovalDetails(props.sRuleId);
  }, [props]);

  // commont code
  const getCommonCodes = async () => {
    setAprTpDivsList((await getCommonCodeNames('APR_TP_DIVS_CD')) ?? []);
    setAprLnRoleList((await getCommonCodeNames('APR_LN_ROLE_CD')) ?? []);
  };

  const fnSearchApprovalDetails = useCallback((params: string) => {
    getApprovalRuleDetails(params).then((result: ApprovalRuleDetail[]) => {
      setRowData(result);
    });
  }, []);

  const btnSave = () => {
    //Validation Start
    const valid = rowData
      .map((rowNode, index) => {
        if (rowNode.aprRuleId != props.sRuleId)
          return `\nLine${index + 1}: ${t(
            'admin.msg.aprRuleId data ERROR!',
            'aprRuleId data ERROR!'
          )}`;
        if (rowNode.aprTpDivsCd == null || rowNode.aprTpDivsCd === '')
          return `\nLine${index + 1}: ${t(
            'admin.msg.Please enter an item:구분값',
            'Please enter an item:구분값'
          )}`;
        if (rowNode.prlYn == null || rowNode.prlYn === '')
          return `\nLine${index + 1}: ${t(
            'admin.msg.Please enter an item:병렬',
            'Please enter an item:병렬'
          )}`;
        if (
          rowNode.aprTpDivsCd != '9' &&
          (rowNode.aprLnRoleCd == null || rowNode.aprLnRoleCd === '')
        )
          return `\nLine${index + 1}: ${t(
            'admin.msg.Please enter an item:대상구분',
            'Please enter an item:대상구분'
          )}`;
        if (rowNode.aprLnRoleCd == 'USER' && (rowNode.userId == null || rowNode.userId === ''))
          return `\nLine${index + 1}: ${t(
            'admin.msg.Please enter an item:사용자',
            'Please enter an item:사용자'
          )}`;
        if (rowNode.aprLnRoleCd == 'TEAM' && (rowNode.deptCd == null || rowNode.deptCd === ''))
          return `\nLine${index + 1}: ${t(
            'admin.msg.Please enter an item:부서',
            'Please enter an item:부서'
          )}`;
        // if (rowNode.aprTpDivsCd != '10' && rowNode.prlYn == 'Y')
        //   return `\nLine${index + 1}: ${t('admin.msg.prlYn data ERROR!', 'prlYn data ERROR!')}`;
      })
      .filter((element) => element !== undefined);
    if (valid.length) {
      openCommonModal({ content: '입력값을 확인해주세요.' + valid.toString() });
      return false;
    }

    const saveRows = rowData;
    // const changedData = saveRows.filter((data) => data.crudKey);
    // if (changedData.length === 0) {
    //   openCommonModal({
    //     content: t('com.label.변경된 내용이 없습니다.', '변경된 내용이 없습니다.'),
    //   });
    //   return false;
    // }

    if (saveRows[saveRows.length - 1].prlYn !== 'N') {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.마지막결재자는 병렬지정할수없습니다.',
          '마지막결재자는 병렬지정할수없습니다.'
        ),
      });
      return false;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        setApprovalRuleDetails(saveRows).then((result) => {
          if (result.successOrNot === 'N') {
            openCommonModal({
              content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            return false;
          }
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          fnSearchApprovalDetails(props.sRuleId);
        });
      },
      noCallback: () => {
        return false;
      },
    });
  };

  const btnAddRow = useCallback(() => {
    if (!props.sRuleId) {
      openCommonModal({
        content: t('admin.msg.결재규칙을 선택해주세요.', '결재규칙을 선택해주세요.'),
      });
      return;
    }
    if (!props.sRuleId) return false;
    const newRow = {
      aprRuleId: props.sRuleId,
      aprTpDivsCd: '0',
      prlYn: 'N',
      aprLnRoleCd: 'USER',
      aprLnChgPsblYn: 'N',
      aprLnDelPsblYn: 'N',
      crudKey: CrudCode.CREATE,
    } as ApprovalRuleDetail;
    setRowData([...rowData, newRow]);
  }, [rowData]);

  const btnDelRow = useCallback(() => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    if (selectedRows.length < 1) return;

    selectedRows.forEach((row) => {
      const item = row.dataItem;
      gridRef.current.collectionView.remove(item);
    });
    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
    gridRef.current.refresh();
  }, [rowData]);

  const dragIndex = useRef<any>();

  const onInitialized = (grid) => {
    gridRef.current = grid;
    // grid.itemFormatter = onItemFormatter;

    grid.formatItem.addHandler((s, e) => {
      const binding = s.columns[e.col].binding;
      if (s.cells === e.panel) {
        if ('sortable' === binding) {
          e.cell.draggable = true;
        }
      }
    });

    grid.hostElement.addEventListener('dragstart', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggingRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
    grid.hostElement.addEventListener('dragover', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        e.preventDefault();
      }
    });
    grid.hostElement.addEventListener('drop', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggedRow(new CellRangeEventArgs(hti.panel, hti.range));

      grid.invalidate();
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        const binding = grid.columns[ht.col].binding;

        // item.crudKey === CrudCode.CREATE
        //사용자
        switch (binding) {
          case 'userInfo':
            setUserModalCondition({
              target: {
                row: ht.row,
                col: ht.col,
              },
              userId: item.userId,
            });
            setEmployeeModalOpen(true); //sssooodsfcsedcsd    l;dskfl;'sdf
            break;
          case 'deptNm':
            setDepartmentModalCondition({
              target: {
                row: ht.row,
                col: ht.col,
              },
              deptCd: item.deptCd,
            });
            setDepartmentModalOpen(true);
            break;
        }
      }
    });
  };

  const onCellEditEnded = (grid, e) => {
    // 바운딩된 컬럼명
    const col = grid.columns[e.col].binding;
    const item = grid.collectionView.currentItem;

    if (col === 'prlYn') {
      //병렬 Y 면 구분 = 병렬합의
      if (item.prlYn === 'Y') {
        item.aprTpDivsCd = '10';
      } else {
        item.aprTpDivsCd = '0';
      }
      grid.collectionView.commitEdit();
    }
  };

  const beginningEdit = useEvent((s, e) => {
    const item = s.rows[e.row].dataItem;
    const col = s.columns[e.col];
    const rowIndex = e.row;

    if ('prlYn' === col.binding && e.row === 0) {
      s.setCellData(rowIndex, col.binding, 'N');
      e.cancel = true;
    }
  });

  const layoutDefinition = useMemo(
    () => [
      {
        header: String(t('com.label.상태', '상태')),
        binding: 'crudKey',
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'sortable',
        header: String(t('com.label.순서변경', '순서변경')),
        width: 80,
        cssClass: 'WijmoCellMove',
        cellTemplate: '<Button />',
        isReadOnly: true,
      },
      // {
      //   header: String(t('admin.grid.라인ID', '라인ID')),
      //   binding: 'aprLnId',
      //   width: 80,
      //   align: 'center',
      //   isReadOnly: true,
      // },
      // {
      //   header: String(t('admin.grid.차수', '차수')),
      //   binding: 'aprLnSnb',
      //   width: 80,
      //   align: 'center',
      //   isReadOnly: true,
      // },
      // {
      //   header: String(t('admin.grid.순번', '순번')),
      //   binding: 'aprLnSeq',
      //   width: 80,
      //   align: 'center',
      //   isReadOnly: true,
      // },
      {
        binding: 'aprTpDivsCd',
        header: `${t('com.grid.구분', '구분')}`,
        align: 'center',
        width: 100,
        cssClass: 'WijmoSelect',
        isReadOnly: true,
        dataMap: new DataMap(aprTpDivsList || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'prlYn',
        header: `${t('com.grid.병렬', '병렬')}`,
        align: 'center',
        width: 60,
        dataMap: new DataMap(commonYNcodes || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        header: String(t('admin.grid.대상구분', '대상구분')),
        binding: 'aprLnRoleCd',
        width: 100,
        align: 'center',
        isReadOnly: true,
        dataMap: new DataMap(aprLnRoleList || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'userInfo',
        header: String(t('admin.grid.사용자', '사용자')),
        align: 'left',
        width: '*',
        minWidth: 100,
        cssClass: 'WijmoFind',
        isReadOnly: true,
        cellTemplate: (params) => `
            <span>${params.value || ''}</span> 
            <Button id='userInfo' /> 
        `,
      },
      {
        binding: 'userId',
        visible: false,
      },
      // {
      //   binding: 'jtiNm',
      //   header: `${t('com.grid.직책/직급호칭', '직책/직급호칭')}`,
      //   align: 'center',
      //   width: 200,
      //   isReadOnly: true,
      // },
      // {
      //   binding: 'deptNm',
      //   header: `${t('com.grid.부서', '부서')}`,
      //   width: 200,
      //   align: 'left',
      //   cssClass: 'WijmoFind',
      //   isReadOnly: true,
      //   cellTemplate: (params) => `
      //       <span>${params.value || ''}</span>
      //       <Button id='deptNm' />
      //   `,
      // },
      // {
      //   header: String(t('admin.grid.결재자 변경가능', '결재자 변경가능')),
      //   binding: 'aprLnChgPsblYn',
      //   width: 80,
      //   align: 'center',
      //   dataMap: new DataMap(commonYNcodes || [], 'cmnCd', 'cmnCdNm'),
      // },
      // {
      //   header: String(t('admin.grid.결재라인 삭제가능', '결재라인 삭제가능')),
      //   binding: 'aprLnDelPsblYn',
      //   width: 80,
      //   align: 'center',
      //   dataMap: new DataMap(commonYNcodes || [], 'cmnCd', 'cmnCdNm'),
      // },
    ],
    [rowData]
  );
  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('admin.label.결재라인', '결재라인')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button css={IconButton.button} className="plus" disableRipple onClick={btnAddRow}>
            {t('com.button.행추가', '행추가')}
          </Button>
          <Button css={IconButton.button} className="minus" disableRipple onClick={btnDelRow}>
            {t('com.button.행삭제', '행삭제')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        rowData={rowData}
        initialized={onInitialized}
        height={250}
        align={'center'}
        beginningEdit={beginningEdit}
        layoutDefinition={layoutDefinition}
        isFilter={false}
        isSelector={true}
        allowPinning={false}
        rowSelection={'single'}
        autoCheck={true}
        cellEditEnded={onCellEditEnded}
        draggingRow={(grid, e) => {
          dragIndex.current = e.row;
          grid.collectionView.moveCurrentToPosition(e.row);
        }}
        draggedRow={(grid, e) => {
          const dropIndex = e.row;
          const arr = grid.collectionView.sourceCollection;
          grid.collectionView.deferUpdate(() => {
            const item = arr[dragIndex.current];
            arr.splice(dragIndex.current, 1);
            arr.splice(dropIndex, 0, item);
            grid.collectionView.moveCurrentToPosition(dropIndex);
            grid.collectionView.items.forEach((item, i) => {
              if (i === 0) {
                item.prlYn = 'N';
                item.aprTpDivsCd = '0'; //  111
              }
            });
          });
        }}
      />

      <GlobalBtnGroup>
        <Button css={IconButton.button} className="save" onClick={btnSave}>
          {t('com.button.저장', '저장')}
        </Button>
      </GlobalBtnGroup>

      {departmentModalOpen && (
        <DepartmentModal
          open={departmentModalOpen}
          close={() => setDepartmentModalOpen(false)}
          save={(department: Department) => {
            const target = departmentModalCondition.target;
            if (target && target?.row > -1) {
              const item = gridRef.current?.rows[target.row].dataItem;
              item.deptCd = department.deptCd;
              item.deptNm = department.deptNm;
              if (item.crudKey !== CrudCode.CREATE) {
                item.crudKey = CrudCode.UPDATE;
              }
              gridRef.current?.collectionView.refresh();
            }
          }}
        />
      )}

      {employeeModalOpen && (
        <UserModal
          open={employeeModalOpen}
          close={() => setEmployeeModalOpen(false)}
          title={t('com.label.사용자조회', '사용자조회')} //
          defaultUserId={userModalCondition?.userId}
          condition={{
            deptNm: '',
            deptCd: '',
          }}
          singleSelect={true}
          isFixedDept={false} //부서 고정 여부
          onCallback={(result) => {
            const target = userModalCondition.target;
            if (target && target?.row > -1) {
              const users = result as Employee;
              const item = gridRef.current?.rows[target.row].dataItem;
              item.userId = users.userId;
              item.empNm = users.empNm;
              item.jtiNm = users.jtiNm;
              item.deptNm = users.deptNm;
              item.deptCd = users.deptCd;
              item.userInfo = users.userInfo;
              if (item.crudKey !== CrudCode.CREATE) {
                item.crudKey = CrudCode.UPDATE;
              }
              gridRef.current?.collectionView.refresh();
            }
          }}
        />
      )}
    </>
  );
};

export default memo(ApprovalLineGrid);
